// CSS
// (imported from src/index.js)

// Alpine.js – lightweight reactive framework, must start early
import Alpine from "alpinejs";
window.Alpine = Alpine;
Alpine.start();

// lazysizes – polyfill lazy loading for non-LCP images
import "lazysizes";

// AOS – animate on scroll (loaded after the page is interactive)
import AOS from "aos";

// Swiper – import only the modules actually used rather than the full bundle.
// This significantly reduces the JS payload served to every visitor.
import Swiper from "swiper";
import { Pagination, Navigation, EffectFade } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import "swiper/css/effect-fade";

// Initialise AOS after the browser has finished loading all resources
window.addEventListener("load", () => {
	// Let JS-enhanced CSS kick in (removes no-js class)
	document.documentElement.classList.remove("no-js");

	AOS.init({
		duration: 600,
		offset: 100,
	});
});

document.addEventListener("DOMContentLoaded", function () {
	const container = document.getElementById("lottie-logo");
	const preloader = document.getElementById("page-preloader");

	const LAST_SEEN_KEY = "lastSeenPreloader";
	const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;
	const lastSeen = localStorage.getItem(LAST_SEEN_KEY);
	const now = Date.now();
	const showPreloader = !lastSeen || now - parseInt(lastSeen, 10) > ONE_WEEK_MS;

	// Always hides the preloader and starts cookie consent.
	// Safe to call multiple times — classList.add is idempotent.
	let preloaderHidden = false;
	function hidePreloader() {
		if (preloaderHidden) return;
		preloaderHidden = true;
		if (preloader) {
			preloader.classList.add("opacity-0");
			preloader.setAttribute("aria-hidden", "true");
			setTimeout(() => {
				preloader.classList.add("hidden");
				startCookieConsent();
			}, 700);
		} else {
			startCookieConsent();
		}
	}

	if (container && preloader) {
		if (showPreloader) {
			localStorage.setItem(LAST_SEEN_KEY, now.toString());

			// Hard deadline: hide the preloader after 5 s regardless of animation state.
			// This prevents a permanent white screen if the Lottie JSON 404s or the
			// dynamic import itself fails (e.g. network error, CSP block).
			const fallbackTimer = setTimeout(hidePreloader, 5000);

			// lottie-web is only needed when the preloader is shown (~1 in 7 sessions).
			// Dynamic import keeps it out of the initial bundle so it never blocks LCP.
			import("lottie-web")
				.then(({ default: lottie }) => {
					lottie
						.loadAnimation({
							container: container,
							renderer: "svg",
							loop: false,
							autoplay: true,
							path: "/app/themes/brandfilm/assets/img/logo.json",
						})
						.addEventListener("complete", () => {
							clearTimeout(fallbackTimer);
							hidePreloader();
						});
				})
				.catch(() => {
					// Import failed — hide immediately so the page isn't blocked.
					clearTimeout(fallbackTimer);
					hidePreloader();
				});
		} else {
			// Preloader already seen this week – hide immediately so content is visible
			preloader.classList.add("hidden");
			preloader.setAttribute("aria-hidden", "true");
			startCookieConsent();
		}
	} else {
		startCookieConsent();
	}

	function startCookieConsent() {
		import("vanilla-cookieconsent").then((CookieConsent) => {
			CookieConsent.run({
				guiOptions: {
					consentModal: {
						layout: "box",
						position: "bottom left",
						equalWeightButtons: true,
						flipButtons: false,
					},
					preferencesModal: {
						layout: "box",
						position: "right",
						equalWeightButtons: true,
						flipButtons: false,
					},
				},
				categories: {
					necessary: { readOnly: true },
					analytics: { enabled: true },
				},
				language: {
					default: "en",
					autoDetect: "browser",
					translations: {
						en: {
							consentModal: {
								title: "We value your privacy",
								description: "This website uses cookies to enhance your experience and improve our services...",
								acceptAllBtn: "Accept all",
								acceptNecessaryBtn: "Reject all",
								showPreferencesBtn: "Manage preferences",
								footer: "",
							},
							preferencesModal: {
								title: "Consent Preferences Center",
								acceptAllBtn: "Accept all",
								acceptNecessaryBtn: "Reject all",
								savePreferencesBtn: "Save preferences",
								closeIconLabel: "Close modal",
								serviceCounterLabel: "Service|Services",
								sections: [],
							},
						},
					},
				},
			});
		});
	}

	// Logo hover animation
	const logo = document.querySelector(".logo");
	const circle = document.getElementById("circle");

	if (logo && circle) {
		const bbox = circle.getBBox();
		const cx = bbox.x + bbox.width / 2;
		const cy = bbox.y + bbox.height / 2;
		circle.style.transformOrigin = `${cx}px ${cy}px`;

		let isAnimating = false;
		logo.addEventListener("mouseenter", () => {
			if (isAnimating) return;
			isAnimating = true;
			circle.style.transition = "transform 1.5s ease-in-out";
			circle.style.transform = "rotate(1080deg)";
			setTimeout(() => {
				circle.style.transition = "none";
				circle.style.transform = "rotate(0deg)";
				isAnimating = false;
			}, 1500);
		});
	}

	// Swiper – now uses tree-shaken modules instead of the full swiper/bundle
	function initSwiper() {
		document.querySelectorAll(".swiper-images").forEach((swiperContainer) => {
			new Swiper(swiperContainer, {
				modules: [Pagination, Navigation, EffectFade],
				pagination: {
					el: swiperContainer.querySelector(".swiper-pagination"),
					clickable: true,
					renderBullet: function (index, className) {
						return '<span class="' + className + '"></span>';
					},
				},
				navigation: {
					nextEl: swiperContainer.querySelector(".swiper-button-next"),
					prevEl: swiperContainer.querySelector(".swiper-button-prev"),
				},
				slidesPerView: 2,
				loop: true,
				speed: 500,
				effect: "fade",
				fadeEffect: { crossFade: true },
			});
		});
	}
	initSwiper();
});

document.addEventListener("DOMContentLoaded", () => {
	// Sector image cycling
	document.querySelectorAll(".titled-sectors").forEach((section) => {
		const imageEl = section.querySelector("#sector-image");
		const sectorEls = section.querySelectorAll(".sector");

		if (!imageEl || sectorEls.length === 0) return;

		const images = Array.from(sectorEls).map((el) => el.dataset.image);
		let current = 0;
		let interval = null;

		function showImage(index) {
			current = index;
			imageEl.src = images[index];
			sectorEls.forEach((el, i) => {
				if (i === index) {
					el.classList.remove("hover:text-salmon");
					el.classList.add("text-salmon");
				} else {
					el.classList.remove("text-salmon");
					el.classList.add("hover:text-salmon");
				}
			});
		}

		function startCycle() {
			interval = setInterval(() => {
				showImage((current + 1) % images.length);
			}, 3000);
		}

		function stopCycle() {
			clearInterval(interval);
		}

		sectorEls.forEach((el, i) => {
			el.dataset.index = i;
			el.addEventListener("mouseenter", () => { stopCycle(); showImage(i); });
			el.addEventListener("mouseleave", () => startCycle());
			el.addEventListener("click", () => { stopCycle(); showImage(i); });
		});

		showImage(0);
		startCycle();
	});

	// Stat counter animation
	const counters = document.querySelectorAll(".stat");
	let animated = false;

	function animateCount(el, targetText, duration = 1500) {
		const hasPlus = targetText.includes("+");
		const hasPercent = targetText.includes("%");
		const numericTarget = parseInt(targetText.replace(/[^\d]/g, ""));
		const startTime = performance.now();

		function update(timestamp) {
			const progress = Math.min((timestamp - startTime) / duration, 1);
			const eased = 1 - Math.pow(1 - progress, 3);
			el.textContent = Math.floor(eased * numericTarget) + (hasPlus ? "+" : hasPercent ? "%" : "");
			if (progress < 1) requestAnimationFrame(update);
		}
		requestAnimationFrame(update);
	}

	function triggerCountUp() {
		if (animated) return;
		const anyVisible = Array.from(counters).some((el) => {
			const rect = el.getBoundingClientRect();
			return rect.top < window.innerHeight && rect.bottom > 0;
		});
		if (anyVisible) {
			animated = true;
			counters.forEach((el) => {
				const target = el.getAttribute("data-count");
				if (target) animateCount(el, target);
			});
		}
	}

	window.addEventListener("scroll", triggerCountUp, { passive: true });
	triggerCountUp();
});

// Mobile navigation drawer toggle
// Drawer markup + close button live in base.twig; the trigger (#mobile-menu-button)
// lives in header.twig. Vanilla — the site bundle no longer ships jQuery.
document.addEventListener("DOMContentLoaded", () => {
	const openBtn = document.getElementById("mobile-menu-button");
	const closeBtn = document.getElementById("mobile-menu-close-button");
	const overlay = document.getElementById("mobile-overlay");
	const drawer = document.getElementById("mobile-nav");

	if (!openBtn || !drawer || !overlay) return;

	let lastFocused = null;

	function openMenu() {
		lastFocused = document.activeElement;
		overlay.classList.remove("hidden");
		drawer.classList.remove("translate-x-full");
		drawer.setAttribute("aria-hidden", "false");
		openBtn.setAttribute("aria-expanded", "true");
		if (closeBtn) closeBtn.setAttribute("aria-expanded", "true");
		document.body.style.overflow = "hidden";
		// Move focus into the drawer for keyboard/AT users
		(closeBtn || drawer.querySelector("a") || drawer).focus();
	}

	function closeMenu() {
		overlay.classList.add("hidden");
		drawer.classList.add("translate-x-full");
		drawer.setAttribute("aria-hidden", "true");
		openBtn.setAttribute("aria-expanded", "false");
		if (closeBtn) closeBtn.setAttribute("aria-expanded", "false");
		document.body.style.overflow = "";
		if (lastFocused && typeof lastFocused.focus === "function") lastFocused.focus();
	}

	openBtn.addEventListener("click", openMenu);
	if (closeBtn) closeBtn.addEventListener("click", closeMenu);
	overlay.addEventListener("click", closeMenu);

	document.addEventListener("keydown", (e) => {
		if (e.key === "Escape" && !drawer.classList.contains("translate-x-full")) {
			closeMenu();
		}
	});

	// Close after following an in-drawer link
	drawer.querySelectorAll("a[href]").forEach((link) => {
		link.addEventListener("click", closeMenu);
	});
});

// Conversion tracking → GA4 (gtag.js is loaded in html-header.twig)
// Fires events GA4 can turn into conversions: WhatsApp "Let's talk" clicks and
// successful contact-form (Ninja Forms) submissions.
document.addEventListener("DOMContentLoaded", () => {
	const track = (name, params) => {
		if (typeof window.gtag === "function") {
			window.gtag("event", name, params || {});
		} else {
			(window.dataLayer = window.dataLayer || []).push({ event: name, ...(params || {}) });
		}
	};

	// "Let's talk" / WhatsApp clicks (button carries data-gtm="lets_talk_whatsapp")
	document.querySelectorAll('[data-gtm="lets_talk_whatsapp"]').forEach((el) => {
		el.addEventListener("click", () =>
			track("lets_talk_click", { method: "whatsapp", link_url: el.getAttribute("href") })
		);
	});

	// Ninja Forms success. Ninja Forms depends on jQuery (shipped by WP), so hook its
	// jQuery event when present rather than adding jQuery to our bundle.
	if (window.jQuery) {
		window.jQuery(document).on("nfFormSubmitResponse", (e, response, formId) => {
			track("contact_submit", { form_id: formId, event_category: "lead" });
		});
	}
});
